<script setup lang="ts">
import { ref, computed, onMounted, defineAsyncComponent } from "vue";
import { usePeopleStore } from "@/stores/people";

const InfoTooltip = defineAsyncComponent(() => import("@/components/Shared/InfoTooltip.vue"));

interface Props {
  person: any;
  tooltip?: boolean;
  autoloadProfilePhoto?: boolean;
  size?: number;
}

const props = withDefaults(defineProps<Props>(), { tooltip: false, autoloadProfilePhoto: false, size: 2 });

const peopleStore = usePeopleStore();

const avatarroot = ref<HTMLElement | null>(null);

const profileImageUri = computed(() => {
  if (props.person.extSysApiUserId) return peopleStore.picture(props.person.extSysApiUserId);
  return peopleStore.picture(props.person.emailSchoolAddress);
});

const imgSize = computed(() => (props.size === 1 ? "w-8 h-8" : "w-11 h-11"));
const rootSize = computed(() => (props.size === 1 ? "w-7 h-7" : "w-10 h-10"));
const textSize = computed(() => (props.size === 1 ? "text-10" : "text-14"));

const displayName = computed(() => {
  if (props.person.name) {
    if (props.person.name.displayName && props.person.name.displayName.length > 0) {
      return props.person.name.displayName;
    }
    return props.person.name.firstName + " " + props.person.name.lastName;
  }
  return "";
});

const initials = computed(() => {
  const indexOfLastSpace = displayName.value.lastIndexOf(" ");
  const lastName = displayName.value.substring(indexOfLastSpace + 1);
  return (displayName.value[0] + (lastName[0] || "")).toUpperCase();
});

const stringToColor = (str: string, saturation: number, lightness: number) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const h = hash % 360;
  return "hsl(" + h + ", " + saturation + "%, " + lightness + "%)";
};

const loadPhoto = async () => {
  peopleStore.loadProfilePicture(props.person);
};

const setupLoadPhotoObserver = () => {
  const options = { root: null, threshold: 0 };
  const observer = new IntersectionObserver(async ([elm]) => {
    if (elm.isIntersecting) {
      await loadPhoto();
      if (avatarroot.value) observer.unobserve(avatarroot.value);
    }
  }, options as any);

  if (avatarroot.value) observer.observe(avatarroot.value);
};

onMounted(() => {
  if (props.autoloadProfilePhoto) {
    setupLoadPhotoObserver();
  } else {
    loadPhoto();
  }
});
</script>

<template>
  <div
    ref="avatarroot"
    class="relative group focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-core rounded-full"
    :class="rootSize"
    :tabindex="tooltip ? 0 : -1"
    :role="tooltip ? 'img' : undefined"
    :aria-label="tooltip ? displayName : undefined"
  >
    <div
      class="bg-cover rounded-full bg-primary-core border border-white-50% flex items-center justify-center overflow-hidden w-full h-full"
      :style="{
        'background-color': stringToColor(displayName, 60, 40)
      }"
    >
      <img
        v-if="profileImageUri"
        :src="profileImageUri"
        class="object-cover rounded-full border border-white-10%"
        :class="imgSize"
        alt=""
      />
      <span v-else class="text-white font-semibold no-underline absolute" :class="textSize" aria-hidden="true">
        {{ initials }}
      </span>
    </div>

    <InfoTooltip v-if="tooltip" :value="displayName"></InfoTooltip>
  </div>
</template>

<style scoped>
img {
  max-width: 2.75rem;
  max-height: 2.75rem;
}
</style>
